/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { assert } from '@fluidframework/common-utils';
import { assertNotUndefined } from '../Common';
import { ChangeTypeInternal, Side, } from './0.0.2';
// Note: Documentation of this constant is merged with documentation of the `ChangeInternal` interface.
/**
 * @public
 */
export const ChangeInternal = {
    build: (source, destination) => ({
        destination,
        source,
        type: ChangeTypeInternal.Build,
    }),
    insert: (source, destination) => ({
        destination,
        source,
        type: ChangeTypeInternal.Insert,
    }),
    detach: (source, destination) => ({
        destination,
        source,
        type: ChangeTypeInternal.Detach,
    }),
    setPayload: (nodeToModify, payload) => ({
        nodeToModify: getNodeId(nodeToModify),
        payload,
        type: ChangeTypeInternal.SetValue,
    }),
    clearPayload: (nodeToModify) => ({
        nodeToModify: getNodeId(nodeToModify),
        // Rationale: 'undefined' is reserved for future use (see 'SetValue' interface above.)
        payload: null,
        type: ChangeTypeInternal.SetValue,
    }),
    constraint: (toConstrain, effect, identityHash, length, contentHash, parentNode, label) => ({
        toConstrain,
        effect,
        identityHash,
        length,
        contentHash,
        parentNode,
        label,
        type: ChangeTypeInternal.Constraint,
    }),
    /** {@inheritdoc (Change:variable).delete } */
    delete: (stableRange) => ChangeInternal.detach(stableRange),
    /** {@inheritdoc (Change:variable).insertTree } */
    insertTree: (nodes, destination) => {
        const build = ChangeInternal.build(nodes, 0);
        return [build, ChangeInternal.insert(build.destination, destination)];
    },
    /** {@inheritdoc (Change:variable).move } */
    move: (source, destination) => {
        const detach = ChangeInternal.detach(source, 0);
        return [detach, ChangeInternal.insert(assertNotUndefined(detach.destination), destination)];
    },
};
/**
 * The remainder of this file consists of factory methods duplicated with those for StableRange/StablePlace and are maintained while
 * the new persisted version of SharedTree ops/summaries is rolled out.
 */
/**
 * @public
 */
export const StablePlaceInternal = {
    /**
     * @returns The location directly before `node`.
     */
    before: (node) => ({
        side: Side.Before,
        referenceSibling: getNodeId(node),
    }),
    /**
     * @returns The location directly after `node`.
     */
    after: (node) => ({
        side: Side.After,
        referenceSibling: getNodeId(node),
    }),
    /**
     * @returns The location at the start of `trait`.
     */
    atStartOf: (trait) => ({
        side: Side.After,
        referenceTrait: trait,
    }),
    /**
     * @returns The location at the end of `trait`.
     */
    atEndOf: (trait) => ({
        side: Side.Before,
        referenceTrait: trait,
    }),
};
/**
 * @public
 */
export const StableRangeInternal = {
    /**
     * Factory for producing a `StableRange` from a start `StablePlace` to an end `StablePlace`.
     * @example
     * StableRange.from(StablePlace.before(startNode)).to(StablePlace.after(endNode))
     */
    from: (start) => ({
        to: (end) => {
            if (start.referenceTrait && end.referenceTrait) {
                assert(start.referenceTrait.parent === end.referenceTrait.parent, 0x65e /* StableRange must be constructed with endpoints from the same trait */);
                assert(start.referenceTrait.label === end.referenceTrait.label, 0x65f /* StableRange must be constructed with endpoints from the same trait */);
            }
            return { start, end };
        },
    }),
    /**
     * @returns a `StableRange` which contains only the provided `node`.
     * Both the start and end `StablePlace` objects used to anchor this `StableRange` are in terms of the passed in node.
     */
    only: (node) => ({
        start: StablePlaceInternal.before(node),
        end: StablePlaceInternal.after(node),
    }),
    /**
     * @returns a `StableRange` which contains everything in the trait.
     * This is anchored using the provided `trait`, and is independent of the actual contents of the trait:
     * it does not use sibling anchoring.
     */
    all: (trait) => ({
        start: StablePlaceInternal.atStartOf(trait),
        end: StablePlaceInternal.atEndOf(trait),
    }),
};
/** The number of IDs that a SharedTree reserves for current or future internal use */
// This value must never change
export const reservedIdCount = 10;
/** The SessionID of the Upgrade Session */
// This UUID must never change
export const ghostSessionId = '79590933-1c70-4fda-817a-adab57c20318';
/** Accepts either a node or a node's identifier, and returns the identifier */
function getNodeId(node) {
    var _a;
    return (_a = node.identifier) !== null && _a !== void 0 ? _a : node;
}
//# sourceMappingURL=0.1.1.js.map